import React, { useState, useEffect } from 'react';
import './Header.css';
import MBLogo from '../../assets/MB_Logo.svg';
import { useAuth } from '../../context/AuthContext';

interface UserInfo {
    firstName?: string;
    lastName?: string;
    email?: string;
}

function Header() {
    const { session, logout, isLoading } = useAuth();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isMicrofrontendView, setIsMicrofrontendView] = useState(false);
    const [currentApp, setCurrentApp] = useState<'ecm' | 'spectre' | null>(null);
    const [isMicrofrontendLoading, setIsMicrofrontendLoading] = useState<boolean>(
        () => Boolean((window as any).__MF_LOADING__)
    );

    // The menu must stay inert while the shell is resolving the session or a microfrontend
    // is still being loaded, otherwise the user can navigate away mid-mount.
    const isMenuDisabled = isLoading || isMicrofrontendLoading;

    // Get user info from auth context
    const userInfo: UserInfo = {
        firstName: session?.firstName || 'User',
        lastName: session?.lastName || '',
        email: session?.email || ''
    };

    useEffect(() => {
        const checkPath = () => {
            const path = window.location.pathname;
            const isEcm = path.startsWith('/ecm');
            const isSpectre = path.startsWith('/spectre');
            setIsMicrofrontendView(isEcm || isSpectre);
            if (isEcm) setCurrentApp('ecm');
            else if (isSpectre) setCurrentApp('spectre');
            else setCurrentApp(null);
        };

        checkPath();

        // Listen for navigation changes
        window.addEventListener('popstate', checkPath);
        window.addEventListener('single-spa:routing-event', checkPath);

        return () => {
            window.removeEventListener('popstate', checkPath);
            window.removeEventListener('single-spa:routing-event', checkPath);
        };
    }, []);

    // Track the microfrontend loading state broadcast from index.tsx
    useEffect(() => {
        const handleLoadingChange = (event: Event) => {
            setIsMicrofrontendLoading(Boolean((event as CustomEvent<boolean>).detail));
        };

        window.addEventListener('mf-loading-change', handleLoadingChange);
        return () => window.removeEventListener('mf-loading-change', handleLoadingChange);
    }, []);

    // Close the drawer if it was open when loading starts
    useEffect(() => {
        if (isMenuDisabled) {
            setIsDrawerOpen(false);
        }
    }, [isMenuDisabled]);

    const getInitials = () => {
        const firstInitial = userInfo?.firstName?.substring(0, 1) || 'U';
        const lastInitial = userInfo?.lastName?.substring(0, 1) || '';
        return `${firstInitial}${lastInitial}`;
    };

    const handleMenuClick = () => {
        if (isMenuDisabled) {
            return;
        }
        setIsDrawerOpen((prev) => !prev);
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
    };

    const handleLogout = async () => {
        setIsDrawerOpen(false);
        await logout();
    };

    const handleSwitchApp = (app: 'ecm' | 'spectre', newTab: boolean) => {
        const path = app === 'ecm' ? '/ecm' : '/spectre';
        if (newTab) {
            window.open(path, '_blank');
        } else {
            window.location.href = path;
        }
        setIsDrawerOpen(false);
    };

    const headerClass = `root-app-header${isMicrofrontendView ? ' transparent' : ''}`;

    return (
        <>
            <header className={headerClass}>
                <div className="header-left">
                    {/* Left section - can be used for navigation/title */}
                </div>

                {/* Only show center logo on landing page, hide in microfrontend view */}
                {!isMicrofrontendView && (
                    <div className="header-center">
                        <img
                            src={MBLogo}
                            alt="Mercedes-Benz Logo"
                            className="header-logo"
                            onClick={() => window.location.href = '/'}
                        />
                    </div>
                )}

                <div className="header-right">
                    <div className="profile-section">
                        {/* Show hamburger menu in microfrontend view, profile avatar otherwise */}
                        {isMicrofrontendView ? (
                            <div
                                className={`hamburger-menu${isMenuDisabled ? ' disabled' : ''}`}
                                onClick={handleMenuClick}
                                aria-disabled={isMenuDisabled}
                            >
                                <span className="hamburger-line"></span>
                                <span className="hamburger-line"></span>
                                <span className="hamburger-line"></span>
                            </div>
                        ) : (
                            <div
                                className={`profile-avatar${isMenuDisabled ? ' disabled' : ''}`}
                                onClick={handleMenuClick}
                                aria-disabled={isMenuDisabled}
                            >
                                <span className="avatar-initials">{getInitials()}</span>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Drawer */}
            {isDrawerOpen && (
                <>
                    <div className="drawer-backdrop" onClick={handleDrawerClose}></div>
                    <div className="drawer-panel">
                        {/* User Information */}
                        <div className="drawer-section">
                            <div className="drawer-user-info">
                                <div className="drawer-avatar">
                                    <span className="avatar-initials">{getInitials()}</span>
                                </div>
                                <div className="drawer-user-details">
                                    <span className="drawer-user-name">
                                        {userInfo?.firstName} {userInfo?.lastName}
                                    </span>
                                    <span className="drawer-user-email">{userInfo?.email}</span>
                                </div>
                            </div>
                        </div>

                        <div className="drawer-divider"></div>

                        {/* Switch App - Only show in microfrontend view and if user has access to other app */}
                        {isMicrofrontendView && currentApp && (
                            <>
                                {/* Show ECM option if currently in Spectre and user has ECM access */}
                                {currentApp === 'spectre' && session?.ecmUser && (
                                    <>
                                        <div className="drawer-section">
                                            <div className="drawer-section-title">Switch Application</div>
                                            <div className="drawer-link-with-submenu">
                                                <div className="drawer-link">
                                                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                                        <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
                                                    </svg>
                                                    <span>Open ECM</span>
                                                    <svg className="submenu-arrow" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                                        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                                                    </svg>
                                                </div>
                                                <div className="drawer-submenu">
                                                    <div className="drawer-submenu-item" onClick={() => handleSwitchApp('ecm', false)}>Current Tab</div>
                                                    <div className="drawer-submenu-item" onClick={() => handleSwitchApp('ecm', true)}>New Tab</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="drawer-divider"></div>
                                    </>
                                )}
                                {/* Show Spectre option if currently in ECM and user has Spectre access */}
                                {currentApp === 'ecm' && session?.spectreUser && (
                                    <>
                                        <div className="drawer-section">
                                            <div className="drawer-section-title">Switch Application</div>
                                            <div className="drawer-link-with-submenu">
                                                <div className="drawer-link">
                                                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                                        <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
                                                    </svg>
                                                    <span>Open Spectre</span>
                                                    <svg className="submenu-arrow" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                                        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                                                    </svg>
                                                </div>
                                                <div className="drawer-submenu">
                                                    <div className="drawer-submenu-item" onClick={() => handleSwitchApp('spectre', false)}>Current Tab</div>
                                                    <div className="drawer-submenu-item" onClick={() => handleSwitchApp('spectre', true)}>New Tab</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="drawer-divider"></div>
                                    </>
                                )}
                            </>
                        )}

                        {/* Logout */}
                        <div className="drawer-section">
                            <div className="drawer-link logout-link" onClick={handleLogout}>
                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                                </svg>
                                <span>Sign Out</span>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

export default Header;
